import React from 'react';
import c from 'classnames';
import InputLabel from '../../lego/InputLabel';
import s from './FormElement.scss';

export interface Props {
  /** The id of the input element. This will be used to link the label to the input field. */
  inputId: string;

  /**
   * A slot rendered at the top of the component. This is wrapped in a `<label />` element.
   *
   * Consider using `<InputLabelText />`
   *
   * Note: This is a required prop for a11y reasons. Pass `isLabelHidden` if designers won't incorporate a visible label.
   */
  label: React.ReactNode;

  /**
   * Label is required for a11y reasons, so this exists to let devs hide it if designers won't work a visible label
   * into the spec.
   */
  isLabelHidden?: boolean;

  /**
   * A slot rendered below `label`, but above `children`.
   *
   * The separation from `label` here is for semantic HTML reasons. This content shouldn't be included inside a `<label />`
   */
  subLabel?: React.ReactNode;

  /**
   * A slot rendered below `children`
   *
   * Consider using `<InputHint />`, `<InputSuccessMessage />` or `<InputErroMessage />`
   */
  details?: React.ReactNode;

  /** tabIndex passed to the label */
  tabIndex?: number;

  /** An optional class name for the container */
  className?: string;

  /** Optionally remove the spacing at the bottom */
  noBottomSpacing?: boolean;

  /** Optionally slim the spacing at the bottom */
  slimBottomSpacing?: boolean;
}

/**
 * Our standard form element layout with a slot above (`label`) and below (`details`) the supplied `children`.
 */
const FormElement = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<Props>
>(
  (
    {
      inputId,
      label,
      subLabel,
      details,
      isLabelHidden,
      tabIndex,
      className,
      children,
      noBottomSpacing = false,
      slimBottomSpacing = false,
    },
    ref
  ) => (
    <div className={c(className, s['root'])} ref={ref}>
      {label && (
        <InputLabel
          className={c(s['label-wrapper'], isLabelHidden && s['hidden'])}
          htmlFor={inputId}
          id={`${inputId}-label`}
          tabIndex={tabIndex}
        >
          {label}
        </InputLabel>
      )}
      {subLabel && <div className={s['sublabel-wrapper']}>{subLabel}</div>}
      {children}
      {details && <div className={s['details-wrapper']}>{details}</div>}
      {!details && !noBottomSpacing && (
        <span
          className={c(
            s['extra-bottom-spacing'],
            slimBottomSpacing && s['slim-bottom-spacing']
          )}
        />
      )}
    </div>
  )
);
FormElement.displayName = 'FormElement';

export default FormElement;
